import React, { useEffect, useRef, useState } from 'react';
import Login from './Login';
import Dashboard from './Dashboard';
import BarDashboard from './BarDashboard';
import RecaudadorDashboard from './RecaudadorDashboard';
import AdminHome from './AdminHome';
import PlayerHome from './PlayerHome';
import TorneosMenu from './TorneosMenu';
import TorneosDashboard from './TorneosDashboard';
import CrearTorneo from './CrearTorneo';
import CrearLiga from './CrearLiga';
import GestionUsuarios from './GestionUsuarios';
import TrainingMenu from './TrainingMenu';
import TopPlayersMenu from './TopPlayersMenu';
import CheckoutTraining from './CheckoutTraining';
import CheckoutDuel from './CheckoutDuel';
import JesusSalateTraining from './JesusSalateTraining';
import JaimeNunezTraining from './JaimeNunezTraining';
import GameSetup from './GameSetup';
import X01Game from './X01Game';
import BotSetup from './BotSetup';
import BotGame from './BotGame';
import OnlineRules from './OnlineRules';
import OnlineLobby from './OnlineLobby';
import OnlineMetrics from './OnlineMetrics';
import OnlineGame from './OnlineGame';
import MatchHistory from './MatchHistory';
import SupportInfo from './SupportInfo';
import MechanicsAnalyzer from './MechanicsAnalyzer';
import TripleGame from './TripleGame';
import CricketGame from './CricketGame';
import ErrorBoundary from './ErrorBoundary';
import api from './api';
import './styles/index.css';

const LEAGUE_RESUME_VIEW_KEY = 'dartleague-league-resume-view';
const TOURNAMENT_RESUME_VIEW_KEY = 'dartleague-tournament-resume-view';

const getSessionNotice = (reason, view, leagueView, tournamentView) => {
  if (view === 'ligas' && leagueView === 'play') {
    return 'Tu sesión había caducado. Inicia sesión de nuevo y te devolvemos al acceso QR de ligas.';
  }

  if (view === 'torneos' && tournamentView === 'play') {
    return 'Tu sesión había caducado. Inicia sesión de nuevo y te devolvemos al acceso QR de campeonatos.';
  }

  if (view === 'ligas') {
    return 'Tu sesión había caducado. Inicia sesión de nuevo para seguir en tus ligas.';
  }

  if (view === 'torneos') {
    return 'Tu sesión había caducado. Inicia sesión de nuevo para seguir en tus campeonatos.';
  }

  if (reason === 'REFRESH_FAILED' || reason === 'REQUEST_UNAUTHORIZED') {
    return 'Tu sesión había caducado. Inicia sesión de nuevo para continuar.';
  }

  return 'Tu sesión se cerró por seguridad. Inicia sesión de nuevo para continuar.';
};

const DartLeagueApp = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userType, setUserType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loginNotice, setLoginNotice] = useState('');
  const [pendingResumeView, setPendingResumeView] = useState(null);
  const [currentView, setCurrentView] = useState(() => {
    if (typeof window === 'undefined') return 'home';
    const saved = localStorage.getItem('dartleague-current-view');
    // Solo restaurar vista si hay partida online activa — el resto siempre al menú principal
    if (saved === 'online-game') return saved;
    return 'home';
  }); // 'home', 'ligas', 'torneos-menu', 'torneos', 'crear-torneo', 'crear-liga', 'x01-setup', 'x01-game'
  const [gameConfig, setGameConfig] = useState(() => {
    // Recuperar gameConfig de sessionStorage tras un refresh
    if (typeof window === 'undefined') return null;
    try {
      const saved = sessionStorage.getItem('dartleague-game-config');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  const currentViewRef = useRef(currentView);
  const leagueViewRef = useRef('overview');
  const tournamentViewRef = useRef('overview');

  useEffect(() => {
    currentViewRef.current = currentView;
  }, [currentView]);

  // Persistir gameConfig en sessionStorage para sobrevivir a refresh
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (gameConfig) {
      sessionStorage.setItem('dartleague-game-config', JSON.stringify(gameConfig));
    } else {
      sessionStorage.removeItem('dartleague-game-config');
    }
  }, [gameConfig]);

  useEffect(() => {
    // Cambiar el título de la página para bookmarks
    document.title = '501 Dart League - Gestión de Ligas de Dardos';
    
    // Actualizar meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Aplicación web para la gestión de ligas de dardos 501. Registra partidas, consulta clasificaciones y gestiona torneos.');
    }

    // Guardar título original para restaurar al desmontar
    const originalTitle = document.title;
    
    return () => {
      // Restaurar título original cuando se salga de la página
      document.title = originalTitle;
    };
  }, []);

  useEffect(() => {
    const cleanup = api.onAuthFailure(({ reason } = {}) => {
      const activeView = currentViewRef.current || 'home';
      const activeLeagueView = leagueViewRef.current || 'overview';
      const activeTournamentView = tournamentViewRef.current || 'overview';

      if (typeof sessionStorage !== 'undefined') {
        if (activeView === 'ligas' && activeLeagueView === 'play') {
          sessionStorage.setItem(LEAGUE_RESUME_VIEW_KEY, activeLeagueView);
          sessionStorage.removeItem(TOURNAMENT_RESUME_VIEW_KEY);
        } else if (activeView === 'torneos' && activeTournamentView === 'play') {
          sessionStorage.setItem(TOURNAMENT_RESUME_VIEW_KEY, activeTournamentView);
          sessionStorage.removeItem(LEAGUE_RESUME_VIEW_KEY);
        } else {
          sessionStorage.removeItem(LEAGUE_RESUME_VIEW_KEY);
          sessionStorage.removeItem(TOURNAMENT_RESUME_VIEW_KEY);
        }
      }

      setPendingResumeView(activeView);
      setLoginNotice(getSessionNotice(reason, activeView, activeLeagueView, activeTournamentView));
      setGameConfig(null);
      setIsAuthenticated(false);
      setUserType(null);
      setCurrentView('home');
    });

    return cleanup;
  }, []);

  useEffect(() => {
    const bootstrapAuth = async () => {
      // Verificar si ya está autenticado
      const authenticated = await api.bootstrapSession();
      setIsAuthenticated(authenticated);

      if (authenticated) {
        const me = await api.getMe();

        if (!me) {
          setIsAuthenticated(false);
          setUserType(null);
          setCurrentView('home');
          setLoading(false);
          return;
        }

        const user = api.getCurrentUser();
        setUserType(user?.tipoUsuario);
      } else {
        setUserType(null);
      }

      setLoading(false);
    };

    bootstrapAuth();
  }, []);

  // Persistir la última vista para que un reload no te lleve al inicio
  useEffect(() => {
    if (typeof window === 'undefined') return;
    localStorage.setItem('dartleague-current-view', currentView);
  }, [currentView]);

  const handleLoginSuccess = async () => {
    await api.getMe();
    const user = api.getCurrentUser();
    setUserType(user?.tipoUsuario);
    setIsAuthenticated(true);

    const resumeView = pendingResumeView;
    setPendingResumeView(null);
    setLoginNotice('');

    if (resumeView) {
      setCurrentView(resumeView);
    }
  };

  const renderLogin = () => (
    <Login
      onLoginSuccess={handleLoginSuccess}
      sessionNotice={loginNotice}
    />
  );

  const handleLeagueViewChange = (nextLeagueView) => {
    leagueViewRef.current = nextLeagueView;
  };

  const handleTournamentViewChange = (nextTournamentView) => {
    tournamentViewRef.current = nextTournamentView;
  };

  if (loading) {
    return (
      <div className="dart-league-app">
        <div className="loading">⏳ Cargando...</div>
      </div>
    );
  }

  // Renderizar vista según estado
  const renderContent = () => {
    // Menú de entrenamientos - disponible siempre
    // X01 Game - partida en curso
    if (currentView === 'x01-game' && gameConfig) {
      return (
        <X01Game
          config={gameConfig}
          onBack={() => {
            setGameConfig(null);
            setCurrentView('training-menu');
          }}
        />
      );
    }

    // Bot Game - partida contra IA en curso
    if (currentView === 'bot-game' && gameConfig) {
      return (
        <BotGame
          config={gameConfig}
          onBack={() => {
            setGameConfig(null);
            setCurrentView('training-menu');
          }}
        />
      );
    }

    // Bot Setup
    if (currentView === 'bot-setup') {
      return (
        <BotSetup
          onBack={() => setCurrentView('training-menu')}
          onStartGame={(config) => {
            setGameConfig(config);
            setCurrentView('bot-game');
          }}
        />
      );
    }

    // X01 Setup
    if (currentView === 'x01-setup') {
      return (
        <GameSetup
          onBack={() => setCurrentView('training-menu')}
          user={api.getCurrentUser()}
          onStartGame={(config) => {
            setGameConfig(config);
            setCurrentView('x01-game');
          }}
        />
      );
    }

    if (currentView === 'training-menu') {
      return (
        <TrainingMenu 
          onBack={() => {
            const previousView = isAuthenticated ? 'home' : 'login';
            localStorage.setItem('dartleague-current-view', previousView);
            setCurrentView(previousView);
          }}
          onSelectCheckout={() => setCurrentView('checkout-training')}
          onSelectCheckoutDuel={() => setCurrentView('checkout-duel')}
          onSelectX01={() => setCurrentView('x01-setup')}
          onSelectTopPlayers={() => setCurrentView('top-players')}
          onSelectJesusSalate={() => setCurrentView('jesus-salate')}
          onSelectMechanics={() => setCurrentView('mechanics-analyzer')}
          onSelectBotGame={() => setCurrentView('bot-setup')}
          onSelectTriples={() => setCurrentView('triple-game')}
          onSelectCricket={() => setCurrentView('cricket-game')}
        />
      );
    }

    // Análisis de mecánica de tiro
    if (currentView === 'mechanics-analyzer') {
      if (!isAuthenticated) return renderLogin();
      return (
        <MechanicsAnalyzer
          onBack={() => setCurrentView('training-menu')}
        />
      );
    }

    // Duelo de Triples
    if (currentView === 'triple-game') {
      return (
        <TripleGame
          onBack={() => setCurrentView('training-menu')}
        />
      );
    }

    // Cricket
    if (currentView === 'cricket-game') {
      return (
        <CricketGame
          onBack={() => setCurrentView('training-menu')}
        />
      );
    }

    // Entrenamiento de cierres - disponible siempre (con o sin login)
    if (currentView === 'checkout-training') {
      return (
        <CheckoutTraining 
          onBack={() => setCurrentView('training-menu')}
        />
      );
    }

    // Duelo / entrenamiento de cierres - disponible siempre
    if (currentView === 'checkout-duel') {
      return (
        <CheckoutDuel
          onBack={() => setCurrentView('training-menu')}
        />
      );
    }

    // Entrenamiento Jesús Salate - disponible siempre
    if (currentView === 'top-players') {
      return (
        <TopPlayersMenu
          onBack={() => setCurrentView('training-menu')}
          onSelectPlayer={(playerId) => {
            if (playerId === 'jesus-salate') setCurrentView('jesus-salate');
            if (playerId === 'jaime-nunez') setCurrentView('jaime-nunez');
          }}
        />
      );
    }

    if (currentView === 'jesus-salate') {
      return (
        <JesusSalateTraining 
          onBack={() => setCurrentView('top-players')}
        />
      );
    }

    if (currentView === 'jaime-nunez') {
      return (
        <JaimeNunezTraining 
          onBack={() => setCurrentView('top-players')}
        />
      );
    }

    // Historial de partidas online
    if (currentView === 'match-history') {
      if (!isAuthenticated) return renderLogin();
      return (
        <MatchHistory
          onBack={() => setCurrentView('home')}
        />
      );
    }

    // Reglas del juego online (requiere login)
    if (currentView === 'online-rules') {
      if (!isAuthenticated) return renderLogin();
      return (
        <OnlineRules
          onBack={() => setCurrentView('home')}
          onEnterLobby={() => setCurrentView('online-lobby')}
          onSupportInfo={() => setCurrentView('support-info')}
        />
      );
    }

    // Información de apoyo al proyecto
    if (currentView === 'support-info') {
      return (
        <SupportInfo
          onBack={() => setCurrentView('online-rules')}
        />
      );
    }

    if (currentView === 'online-metrics') {
      if (!isAuthenticated || api.getCurrentUser()?.username?.toLowerCase() !== 'juanmi') return renderLogin();
      return <OnlineMetrics onBack={() => setCurrentView('online-lobby')} />;
    }

    // Lobby online (sala de espera)
    if (currentView === 'online-lobby') {
      if (!isAuthenticated) return renderLogin();
      return (
        <OnlineLobby
          onBack={() => setCurrentView('home')}
          onGameStart={(gameSetup) => {
            setGameConfig(gameSetup);
            setCurrentView('online-game');
          }}
          onGoToMetrics={() => setCurrentView('online-metrics')}
        />
      );
    }

    // Partida online en curso
    if (currentView === 'online-game') {
      if (!isAuthenticated) return renderLogin();
      const currentUser = api.getCurrentUser();
      return (
        <OnlineGame
          gameSetup={gameConfig}
          currentUserId={currentUser?._id}
          onExit={() => {
            setGameConfig(null);
            setCurrentView('online-lobby');
          }}
          onGameSetupRecovered={(recoveredSetup) => {
            setGameConfig(recoveredSetup);
          }}
          onRematch={(newGameSetup) => {
            setGameConfig(newGameSetup);
          }}
        />
      );
    }

    if (!isAuthenticated) {
      return renderLogin();
    }

    // Bar y recaudador comparten el flujo de admin; el recaudador añade el control de partidas
    if (userType === 'bar' || userType === 'recaudador') {
      const esRecaudador = userType === 'recaudador';
      const goToControlPartidas = esRecaudador ? () => setCurrentView('control-partidas') : undefined;

      if (esRecaudador && currentView === 'control-partidas') {
        return (
          <RecaudadorDashboard
            onBack={() => setCurrentView('home')}
            onLogout={() => {
              setIsAuthenticated(false);
              setUserType(null);
              setCurrentView('home');
            }}
          />
        );
      }

      // Pantalla principal de Admin
      if (currentView === 'home') {
        return (
          <AdminHome 
            onGoToLigas={() => setCurrentView('ligas')}
            onGoToTorneos={() => setCurrentView('torneos')}
            onCrearLiga={() => setCurrentView('crear-liga')}
            onCrearTorneo={() => setCurrentView('crear-torneo')}
            onGoToGestionUsuarios={() => setCurrentView('gestion-usuarios')}
            onGoToTraining={() => setCurrentView('training-menu')}
            onGoToControlPartidas={goToControlPartidas}
            onLogout={() => {
              setIsAuthenticated(false);
              setUserType(null);
            }}
          />
        );
      }

      // Gestión de usuarios
      if (currentView === 'gestion-usuarios') {
        return (
          <GestionUsuarios 
            onBack={() => setCurrentView('home')}
          />
        );
      }

      // Vista de ligas (BarDashboard)
      if (currentView === 'ligas') {
        return (
          <BarDashboard 
            onGoToTorneos={esRecaudador ? undefined : () => setCurrentView('torneos')}
            onBack={() => setCurrentView('home')}
          />
        );
      }

      // Vista de torneos
      if (currentView === 'torneos') {
        return (
          <TorneosDashboard 
            onBack={() => setCurrentView('home')}
            onTournamentViewChange={handleTournamentViewChange}
          />
        );
      }

      // Crear liga
      if (currentView === 'crear-liga') {
        return (
          <CrearLiga 
            onBack={() => setCurrentView('home')}
            onLigaCreada={() => setCurrentView('ligas')}
          />
        );
      }

      // Crear torneo
      if (currentView === 'crear-torneo') {
        return (
          <CrearTorneo 
            onBack={() => setCurrentView('home')}
            onTorneoCreado={() => setCurrentView('torneos')}
          />
        );
      }

      // Menú de torneos (legacy - por si acaso)
      if (currentView === 'torneos-menu') {
        return (
          <TorneosMenu 
            onBack={() => setCurrentView('home')}
            onVerTorneos={() => setCurrentView('torneos')}
            onCrearTorneo={() => setCurrentView('crear-torneo')}
          />
        );
      }

      // Default para bar: home
      return (
        <AdminHome 
          onGoToLigas={() => setCurrentView('ligas')}
          onGoToTorneos={() => setCurrentView('torneos')}
          onCrearLiga={() => setCurrentView('crear-liga')}
          onCrearTorneo={() => setCurrentView('crear-torneo')}
          onGoToGestionUsuarios={() => setCurrentView('gestion-usuarios')}
          onGoToTraining={() => setCurrentView('training-menu')}
          onGoToControlPartidas={goToControlPartidas}
          onLogout={() => {
            setIsAuthenticated(false);
            setUserType(null);
          }}
        />
      );
    }

    // Si es jugador normal
    // Pantalla principal del jugador
    if (currentView === 'home') {
      return (
        <PlayerHome 
          onGoToLigas={() => setCurrentView('ligas')}
          onGoToTorneos={() => setCurrentView('torneos')}
          onGoToTraining={() => setCurrentView('training-menu')}
          onGoToOnline={() => setCurrentView('online-rules')}
          onGoToMatchHistory={() => setCurrentView('match-history')}
          onLogout={() => {
            setIsAuthenticated(false);
            setUserType(null);
          }}
        />
      );
    }

    // Vista de torneos
    if (currentView === 'torneos') {
      return (
        <TorneosDashboard 
          onBack={() => setCurrentView('home')}
          onTournamentViewChange={handleTournamentViewChange}
        />
      );
    }

    // Vista de ligas para jugadores
    if (currentView === 'ligas') {
      return (
        <Dashboard 
          onBack={() => setCurrentView('home')}
          onLeagueViewChange={handleLeagueViewChange}
        />
      );
    }

    // Default: ir a home
    return (
      <PlayerHome 
        onGoToLigas={() => setCurrentView('ligas')}
        onGoToTorneos={() => setCurrentView('torneos')}
        onGoToTraining={() => setCurrentView('training-menu')}
        onGoToOnline={() => setCurrentView('online-rules')}
        onGoToMatchHistory={() => setCurrentView('match-history')}
        onLogout={() => {
          setIsAuthenticated(false);
          setUserType(null);
        }}
      />
    );
  };

  return (
    <ErrorBoundary>
      <div className="dart-league-app">
        {renderContent()}
      </div>
    </ErrorBoundary>
  );
};

export default DartLeagueApp;
